import React, { useState } from "react";
import { CampaignSubmissionModal } from "components/CampaignSubmissionModal";
import { SuccessIcon } from "components/icons/components/SuccessIcon";
// eslint-disable-next-line import/no-unresolved -- JS sidebar module resolved at build time
import CheckoutSidebar from "campaign-builder/checkout/sidebar";
import CheckoutContent from "campaign-builder/checkout/CheckoutContent";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { getPaymentSkipStatus } from "utils/paymentSkip";
import type { ChannelConfig } from "campaign-builder/config";
import { getPostSubmitUrl } from "campaign-builder/postSubmitUrl";
import type { DiscountChoice } from "campaign-builder/channels/directmail/adm/order-details/reviewOffer";
import { OrderDetailsDrawer } from "./OrderDetailsDrawer";
import { useChannelSubmit } from "./useChannelSubmit";
import { useBillingGateSurface } from "campaign-builder/checkout/billing-profile/useBillingGateSurface";

interface OrderDetailsFlowProps {
  open: boolean;
  onClose: () => void;
  config: ChannelConfig;
  clientId: string;
}

/**
 * The order-details submission flow for H2H/D2D: drawer → ToS → Stripe checkout,
 * or a single skip-Stripe submit. Lives inside the TargetingProvider so the cost +
 * submit hooks can read the current selection. The mount only toggles `open`.
 */
export const OrderDetailsFlow = ({ open, onClose, config, clientId }: OrderDetailsFlowProps) => {
  const { campaignData } = useCampaignData();
  const { client } = useClientConfig();
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);
  // The gate banner also rides the review drawer; the wall is at payment, so
  // "Go to payment" is never blocked here.
  const billingGate = useBillingGateSurface(clientId);
  // HUB-440: lifted from OrderDetailsDrawer's useDiscountExclusivity so
  // useChannelSubmit can apply the review discount at checkout. Mirrors AdmBuilder.
  const [discountChoice, setDiscountChoice] = useState<DiscountChoice>(null);

  const isPaymentSkipped = getPaymentSkipStatus(campaignData, client);
  const {
    onSubmit,
    showCheckout,
    setShowCheckout,
    costBreakdown,
    breakdownReady,
    couponCode,
    applyReviewDiscount,
    campaignId,
    countryCode,
  } = useChannelSubmit({ config, setIsCampaignSubmissionModalOpen: setIsSubmissionOpen, discountChoice });

  const handleGoToPayment = async () => {
    // The terms acknowledgement is now the inline note under the drawer's
    // button (TermsAgreementNote), so this goes straight to submission.
    onClose();
    await onSubmit();
  };

  return (
    <>
      <OrderDetailsDrawer
        open={open}
        onClose={onClose}
        config={config}
        isPaymentSkipped={isPaymentSkipped}
        onGoToPayment={handleGoToPayment}
        showPurchaseOrder
        topBanner={billingGate.renderBanner("flyering-order-details")}
        onDiscountChoiceChange={setDiscountChoice}
      />
      {billingGate.drawer}
      <CheckoutSidebar show={showCheckout} onClose={() => setShowCheckout(false)}>
        {showCheckout && (
          <CheckoutContent
            campaignId={campaignId}
            clientId={clientId}
            breakdown={costBreakdown ?? null}
            breakdownReady={breakdownReady}
            couponCode={couponCode}
            applyReviewDiscount={applyReviewDiscount}
            countryCode={countryCode}
            onClose={() => setShowCheckout(false)}
            // After a successful payment the checkout shows a terminal success screen (no close/finish) and then sends the client out of the builder, so they can't return to the paid campaign and pay again (PAD-31453).
            onPaymentSuccess={() => window.location.replace(getPostSubmitUrl())}
          />
        )}
      </CheckoutSidebar>
      <CampaignSubmissionModal
        isOpen={isSubmissionOpen}
        onConfirmation={() => {
          setIsSubmissionOpen(false);
          window.location.replace(getPostSubmitUrl());
        }}
        title="Campaign submitted!"
        body="We'll review and confirm your campaign soon."
        icon={SuccessIcon}
        ctaText="Back to home"
      />
    </>
  );
};
